import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import NotesFab from '@/components/NotesFab';
import WhatsNewModal from '@/components/WhatsNewModal';
import CostTrackerWidget from '@/components/CostTrackerWidget';
import ActiveUsersWidget from '@/components/ActiveUsersWidget';
import NotificationBell from '@/components/NotificationBell';
import JobsFab from '@/components/JobsFab';
import { trackPageView } from '@/lib/usageTracker';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  LayoutGrid, 
  PlusCircle, 
  ListTodo, 
  FolderOpen, 
  Cog, 
  LogOut, 
  User,
  Menu,
  X,
  ChevronRight,
  ScanSearch,
  Sparkles,
  FileImage,
  Building2,
  Activity,
  Bell,
  Film,
  Zap,
  CheckCircle2,
  Gift,
  Lightbulb,
  Send,
  Barcode,
  Rocket,
  Package,
  Bug,
  Star,
  UserRound,
  Briefcase,
  Dna,
  ShieldCheck,
  Palette,
  Rocket as RocketLaunch,
  Brain,
  Radar,
  Library,
  SlidersHorizontal,
  LayoutPanelTop
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for dynamic icon rendering
const ICON_MAP = {
  Rocket: Rocket,
  Barcode: Barcode,
  Film: Film,
  Zap: Zap,
  CheckCircle2: CheckCircle2,
  Gift: Gift,
  Sparkles: Sparkles,
  FileImage: FileImage,
  Package: Package,
  Bug: Bug,
  Star: Star,
  Activity: Activity
};

const SectionHeader = ({ label, icon: Icon, expanded, onToggle, beta, testId, isActive }) => {
  return (
    <button
      onClick={onToggle}
      className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl border transition-all duration-200 group ${
        expanded
          ? 'bg-gradient-to-br from-[#2EB4AC]/[0.06] to-cyan-50/60 dark:from-[#2EB4AC]/15 dark:to-slate-900 border-dashed border-[#2EB4AC]/50 dark:border-[#2EB4AC]/60 shadow-[0_0_0_3px_rgba(46,180,172,0.08)]'
          : isActive
            ? 'bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 shadow-sm hover:border-[#2EB4AC]/40'
            : 'bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-800 hover:border-slate-300 hover:shadow-sm'
      }`}
      data-testid={testId}
    >
      {Icon && (
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
          expanded || isActive
            ? 'bg-[#2EB4AC]/12 text-[#2EB4AC]'
            : 'bg-slate-100 text-slate-500 group-hover:bg-[#2EB4AC]/10 group-hover:text-[#2EB4AC]'
        }`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      )}
      <span className={`text-[11px] font-bold uppercase tracking-[0.08em] flex-1 text-left ${
        expanded || isActive ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400'
      }`}>
        {label}
      </span>
      {beta && (
        <span className="px-1.5 py-0.5 text-[8px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full uppercase tracking-wide">Beta</span>
      )}
      <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-200 ${
        expanded ? 'rotate-90 text-[#2EB4AC]' : 'text-slate-400 group-hover:text-slate-600'
      }`} />
    </button>
  );
};

const NavItem = ({ to, icon: Icon, children, badge, badgeKey }) => {
  const [showBadge, setShowBadge] = useState(true);
  useEffect(() => {
    if (!badge || !badgeKey) return;
    try {
      let firstSeen = localStorage.getItem(badgeKey);
      if (!firstSeen) {
        firstSeen = new Date().toISOString();
        localStorage.setItem(badgeKey, firstSeen);
      }
      const ageDays = (Date.now() - new Date(firstSeen).getTime()) / (1000 * 60 * 60 * 24);
      setShowBadge(ageDays < 7);
    } catch (e) { /* ignore */ }
  }, [badge, badgeKey]);

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
          isActive
            ? 'bg-[#2EB4AC] text-white shadow-md shadow-[#2EB4AC]/20'
            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className="h-4 w-4" />
          <span className="flex-1">{children}</span>
          {badge && (!badgeKey || showBadge) && (
            <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded-full uppercase tracking-wide ${
              isActive
                ? 'bg-white/25 text-white'
                : 'bg-gradient-to-r from-[#2EB4AC] to-emerald-500 text-white animate-pulse'
            }`}>
              {badge}
            </span>
          )}
          <ChevronRight className={`h-3 w-3 transition-transform ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`} />
        </>
      )}
    </NavLink>
  );
};

export const DashboardLayout = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Collapses the floating widget stack (Notes, Notifications, Jobs) into a
  // single drawer toggle so it doesn't block the right edge of the page.
  const [widgetsOpen, setWidgetsOpen] = useState(false);
  const [brandManagerExpanded, setBrandManagerExpanded] = useState(false);
  const [assigneeTrackerExpanded, setAssigneeTrackerExpanded] = useState(false);
  const [brandWorkspaceExpanded, setBrandWorkspaceExpanded] = useState(false);
  const [brands, setBrands] = useState([]);
  const [assignees, setAssignees] = useState([]);

  // Top-level sidebar section collapsed state (default all collapsed)
  const [sections, setSections] = useState({
    quality: false,
    creative: false,
    launchpad: false,
    intel: false,
    settings: false,
  });
  const toggleSection = (key) => setSections(p => ({ ...p, [key]: !p[key] }));

  // "NEW" badge for Assignee Tracker valid for 7 days from first view
  const ASSIGNEE_TRACKER_NEW_KEY = 'prism_assignee_tracker_first_seen';
  const [showAssigneeTrackerNew, setShowAssigneeTrackerNew] = useState(true);
  useEffect(() => {
    try {
      let firstSeen = localStorage.getItem(ASSIGNEE_TRACKER_NEW_KEY);
      if (!firstSeen) {
        firstSeen = new Date().toISOString();
        localStorage.setItem(ASSIGNEE_TRACKER_NEW_KEY, firstSeen);
      }
      const ageDays = (Date.now() - new Date(firstSeen).getTime()) / (1000 * 60 * 60 * 24);
      setShowAssigneeTrackerNew(ageDays < 7);
    } catch (e) { /* localStorage not available */ }
  }, []);
  
  // Feature Request Modal State
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [featureTitle, setFeatureTitle] = useState('');
  const [featureDescription, setFeatureDescription] = useState('');
  const [submittingFeature, setSubmittingFeature] = useState(false);

  // App Updates State
  const [appUpdates, setAppUpdates] = useState([]);

  // Fetch app updates
  useEffect(() => {
    const fetchAppUpdates = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API_URL}/api/app-updates`, { 
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAppUpdates(data);
        } else {
          // Seed initial data if empty
          await fetch(`${API_URL}/api/app-updates/seed`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          // Fetch again
          const res2 = await fetch(`${API_URL}/api/app-updates`, { 
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res2.ok) {
            const data = await res2.json();
            setAppUpdates(data);
          }
        }
      } catch (error) {
        console.error('Error fetching app updates:', error);
      }
    };
    fetchAppUpdates();
  }, []);

  // Fetch brands for brand manager submenu
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await fetch(`${API_URL}/api/masters/brands`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setBrands(data);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
      }
    };
    fetchBrands();
  }, []);

  // Fetch assignees for assignee tracker submenu
  useEffect(() => {
    const fetchAssignees = async () => {
      try {
        const res = await fetch(`${API_URL}/api/masters/assignees`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setAssignees(data);
        }
      } catch (error) {
        console.error('Error fetching assignees:', error);
      }
    };
    fetchAssignees();
  }, []);

  // Auto-expand brand manager section if on a brand manager route
  useEffect(() => {
    if (location.pathname.startsWith('/tasks/brand/')) {
      setBrandWorkspaceExpanded(true);
      setBrandManagerExpanded(true);
    }
    if (location.pathname.startsWith('/assignee/')) {
      setBrandWorkspaceExpanded(true);
      setAssigneeTrackerExpanded(true);
    }
  }, [location.pathname]);

  // Auto-expand the relevant top-level section based on current route
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const p = location.pathname;
    const next = { ...sections };
    if (p.startsWith('/listing-quality') || p.startsWith('/sku-live-status') || p.startsWith('/sku-dna')) next.quality = true;
    if (p === '/dashboard' || p.startsWith('/submit') || p.startsWith('/tasks') || p.startsWith('/assignee') || p.startsWith('/asset-library')) next.creative = true;
    if (p.startsWith('/ean-request') || p.startsWith('/new-product-launch')) next.launchpad = true;
    if (p.startsWith('/creative-analyzer') || p.startsWith('/creative-log') || p.startsWith('/competitor-radar') || p.startsWith('/competitor-repository')) next.intel = true;
    if (p.startsWith('/settings')) next.settings = true;
    setSections(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleFeatureSubmit = async () => {
    if (!featureTitle.trim() || !featureDescription.trim()) {
      toast.error('Please fill in both title and description');
      return;
    }
    
    setSubmittingFeature(true);
    try {
      const response = await fetch(`${API_URL}/api/feature-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: featureTitle,
          description: featureDescription,
          submitted_by: user?.email || 'Unknown'
        })
      });
      
      if (response.ok) {
        toast.success('Feature request submitted! Thank you for your feedback.');
        setFeatureTitle('');
        setFeatureDescription('');
        setFeatureModalOpen(false);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit feature request. Please try again.');
    } finally {
      setSubmittingFeature(false);
    }
  };

  const pathname = location.pathname;
  const sectionActive = {
    quality: pathname.startsWith('/listing-quality') || pathname.startsWith('/sku-live-status') || pathname.startsWith('/sku-dna'),
    creative: pathname === '/dashboard' || pathname.startsWith('/submit') || pathname.startsWith('/tasks') || pathname.startsWith('/assignee') || pathname.startsWith('/asset-library'),
    launchpad: pathname.startsWith('/ean-request') || pathname.startsWith('/new-product-launch'),
    intel: pathname.startsWith('/creative-analyzer') || pathname.startsWith('/creative-log') || pathname.startsWith('/competitor-radar') || pathname.startsWith('/competitor-repository'),
    settings: pathname.startsWith('/settings'),
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-300 ease-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo Section - Bigger & Prominent */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <NavLink to="/dashboard" className="flex items-center gap-3 hover:opacity-90 transition-opacity cursor-pointer">
                <div className="w-12 h-12 rounded-xl bg-[#2EB4AC] flex items-center justify-center shadow-lg shadow-[#2EB4AC]/20">
                  <span className="font-black text-xl text-white">P</span>
                </div>
                <div>
                  <h1 className="font-bold text-2xl text-[#2EB4AC] tracking-tight">
                    PRISM
                  </h1>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] text-muted-foreground">by</span>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_dafbd9f2-4a1c-4bf6-a0fc-53687b91e9c2/artifacts/xpqc0ett_Wellversed%20Complete%20Logo%20Blue%20no-tagline%20%282%29.png" 
                      alt="Wellversed" 
                      className="h-4 object-contain"
                    />
                  </div>
                </div>
              </NavLink>
              <button 
                className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-5 space-y-3 overflow-y-auto">
            {/* Quality Control Section */}
            <SectionHeader label="Quality Control" icon={ShieldCheck} expanded={sections.quality} isActive={sectionActive.quality} onToggle={() => toggleSection('quality')} testId="section-quality" />
            {sections.quality && (
              <div className="space-y-1 pl-3 pt-2 ml-3 border-l-2 border-dashed border-[#2EB4AC]/40">
                <NavItem to="/listing-quality" icon={ScanSearch}>PDP Checker</NavItem>
                <NavItem to="/sku-live-status" icon={Activity}>SKUs Live Status</NavItem>
                <NavItem to="/sku-dna" icon={Dna} badge="NEW" badgeKey="prism_skus_dna_first_seen">SKUs DNA</NavItem>
              </div>
            )}

            {/* Creative Task Manager Section */}
            <SectionHeader label="Creative Task Manager" icon={Palette} expanded={sections.creative} isActive={sectionActive.creative} onToggle={() => toggleSection('creative')} testId="section-creative" />
            {sections.creative && (
              <div className="space-y-1 pl-3 pt-2 ml-3 border-l-2 border-dashed border-[#2EB4AC]/40">
                <NavItem to="/dashboard" icon={LayoutGrid}>Dashboard</NavItem>
                <NavItem to="/submit" icon={PlusCircle}>Submit Task</NavItem>
                <NavItem to="/tasks" icon={ListTodo}>Task Log</NavItem>
                <NavItem to="/asset-library" icon={FolderOpen} badge="NEW" badgeKey="prism_asset_library_first_seen">Asset Library</NavItem>

                {/* Brand Workspace Parent Section (contains Brands Task Tracker + Assignee Tracker) */}
                <div className="mt-1">
                  <button
                    onClick={() => setBrandWorkspaceExpanded(!brandWorkspaceExpanded)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
                    data-testid="brand-workspace-parent-toggle"
                  >
                    <Building2 className="h-4 w-4" />
                    <span className="flex-1 text-left">Brand Workspace</span>
                    <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${brandWorkspaceExpanded ? 'rotate-90' : ''}`} />
                  </button>

                  {brandWorkspaceExpanded && (
                    <div className="ml-3 pl-2 border-l-2 border-slate-200 dark:border-slate-700 mt-1 space-y-0.5">
                      {/* Sub: Brands Task Tracker */}
                      <button
                        onClick={() => setBrandManagerExpanded(!brandManagerExpanded)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-md transition-all text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                        data-testid="brands-task-tracker-toggle"
                      >
                        <Briefcase className="h-3.5 w-3.5" />
                        <span className="flex-1 text-left">Brands Task Tracker</span>
                        <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${brandManagerExpanded ? 'rotate-90' : ''}`} />
                      </button>
                      {brandManagerExpanded && (
                        <div className="ml-3 pl-2 border-l border-slate-200 space-y-0.5 mt-0.5 mb-1">
                          {brands.map(brand => (
                            <NavLink
                              key={brand.id}
                              to={`/tasks/brand/${encodeURIComponent(brand.name)}`}
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                                  isActive
                                    ? 'bg-[#2EB4AC]/10 text-[#2EB4AC]'
                                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                                }`
                              }
                            >
                              {brand.icon_url ? (
                                <img src={brand.icon_url} alt={brand.name} className="w-4 h-4 rounded object-contain" />
                              ) : (
                                <div className="w-4 h-4 rounded bg-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-500">
                                  {brand.name.charAt(0)}
                                </div>
                              )}
                              <span className="truncate">{brand.name}</span>
                            </NavLink>
                          ))}
                          {brands.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No brands configured</p>}
                        </div>
                      )}

                      {/* Sub: Assignee Tracker */}
                      <button
                        onClick={() => setAssigneeTrackerExpanded(!assigneeTrackerExpanded)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-md transition-all text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                        data-testid="assignee-tracker-toggle"
                      >
                        <UserRound className="h-3.5 w-3.5" />
                        <span className="flex-1 text-left flex items-center gap-1.5">
                          Assignee Tracker
                          {showAssigneeTrackerNew && (
                            <span className="px-1.5 py-0.5 text-[9px] font-bold bg-gradient-to-r from-[#2EB4AC] to-emerald-500 text-white rounded-full uppercase tracking-wide animate-pulse">New</span>
                          )}
                        </span>
                        <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${assigneeTrackerExpanded ? 'rotate-90' : ''}`} />
                      </button>
                      {assigneeTrackerExpanded && (
                        <div className="ml-3 pl-2 border-l border-slate-200 space-y-0.5 mt-0.5">
                          {assignees.map(a => {
                            const initials = (a.name || '').match(/\b\w/g)?.slice(0, 2).join('').toUpperCase() || '?';
                            return (
                              <NavLink
                                key={a.id}
                                to={`/assignee/${a.id}`}
                                className={({ isActive }) =>
                                  `flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                                    isActive
                                      ? 'bg-[#2EB4AC]/10 text-[#2EB4AC]'
                                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                                  }`
                                }
                                data-testid={`assignee-nav-${a.id}`}
                              >
                                <div className="w-4 h-4 rounded-full bg-[#2EB4AC]/15 text-[#2EB4AC] flex items-center justify-center text-[8px] font-bold">
                                  {initials}
                                </div>
                                <span className="truncate">{a.name}</span>
                              </NavLink>
                            );
                          })}
                          {assignees.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No assignees configured</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Product Launchpad Section */}
            <SectionHeader label="Product Launchpad" icon={RocketLaunch} beta expanded={sections.launchpad} isActive={sectionActive.launchpad} onToggle={() => toggleSection('launchpad')} testId="section-launchpad" />
            {sections.launchpad && (
              <div className="space-y-1 pl-3 pt-2 ml-3 border-l-2 border-dashed border-[#2EB4AC]/40">
                <NavItem to="/ean-request" icon={Barcode}>EAN Request</NavItem>
                <NavItem to="/new-product-launch" icon={Rocket}>New Product Launch</NavItem>
              </div>
            )}

            {/* Creative Intelligence Section */}
            <SectionHeader label="Creative Intelligence" icon={Brain} beta expanded={sections.intel} isActive={sectionActive.intel} onToggle={() => toggleSection('intel')} testId="section-intel" />
            {sections.intel && (
              <div className="space-y-1 pl-3 pt-2 ml-3 border-l-2 border-dashed border-[#2EB4AC]/40">
                <NavItem to="/creative-analyzer" icon={Sparkles}>Analyze Creative</NavItem>
                <NavItem to="/creative-log" icon={FileImage}>Creative Log</NavItem>
                <NavItem to="/competitor-radar" icon={Radar} badge="NEW" badgeKey="prism_competitor_radar_first_seen">Competitor Radar</NavItem>
                <NavItem to="/competitor-repository" icon={Library} badge="NEW" badgeKey="prism_competitor_repo_first_seen">Competitor Repository</NavItem>
              </div>
            )}

            {/* Settings Section */}
            <SectionHeader label="Settings" icon={SlidersHorizontal} expanded={sections.settings} isActive={sectionActive.settings} onToggle={() => toggleSection('settings')} testId="section-settings" />
            {sections.settings && (
              <div className="space-y-1 pl-3 pt-2 ml-3 border-l-2 border-dashed border-[#2EB4AC]/40">
                <NavItem to="/settings" icon={Cog}>Control Panel</NavItem>
              </div>
            )}
          </nav>

          {/* Feature Request - Sleek with visual cue */}
          <div className="px-3 py-3 border-t border-border">
            <button
              onClick={() => setFeatureModalOpen(true)}
              className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gradient-to-r from-amber-50/80 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/10 border border-amber-200/30 dark:border-amber-800/20 hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm hover:shadow-amber-500/10 transition-all duration-200"
            >
              <div 
                className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center group-hover:scale-105 transition-transform"
                style={{ animation: 'bulbGlow 2s ease-in-out infinite' }}
              >
                <Lightbulb className="h-4 w-4 text-white" />
              </div>
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">Request a Feature</span>
              <Sparkles className="h-3.5 w-3.5 text-amber-400/50 ml-auto group-hover:text-amber-500 transition-colors" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 h-14 bg-background/80 backdrop-blur-xl border-b border-border" data-testid="top-header">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            <button 
              className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </button>

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              {/* Active users (live presence) */}
              <ActiveUsersWidget />

              {/* Cost Tracker pill */}
              <CostTrackerWidget />

              {/* What's New Notifications */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="relative h-10 w-10 rounded-full hover:bg-violet-50 transition-all duration-200"
                    data-testid="whats-new-btn"
                  >
                    <Sparkles className="h-5 w-5 text-slate-500 hover:text-violet-600 transition-colors" 
                      style={{
                        animation: appUpdates.some(u => u.is_new) ? 'wiggle 0.5s ease-in-out infinite' : 'none',
                        transformOrigin: 'top center'
                      }}
                    />
                    {appUpdates.some(u => u.is_new) && (
                      <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-violet-500 rounded-full border-2 border-white" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-0" sideOffset={8}>
                  <div className="px-4 py-3 border-b border-border bg-gradient-to-r from-violet-500/10 to-purple-500/10">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-violet-500" />
                      <h3 className="font-semibold text-sm">What's New</h3>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">Recent updates & improvements</p>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {appUpdates.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                        No updates yet
                      </div>
                    ) : (
                      appUpdates.map((update, idx) => {
                        const IconComponent = ICON_MAP[update.icon] || Sparkles;
                        const updateDate = update.created_at ? new Date(update.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
                        return (
                          <div 
                            key={update.id}
                            className={`px-4 py-3 hover:bg-accent/50 transition-colors ${
                              idx !== appUpdates.length - 1 ? 'border-b border-border/50' : ''
                            }`}
                          >
                            <div className="flex gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                update.type === 'feature' 
                                  ? 'bg-violet-100 text-violet-600' 
                                  : update.type === 'bugfix'
                                  ? 'bg-red-100 text-red-600'
                                  : 'bg-amber-100 text-amber-600'
                              }`}>
                                <IconComponent className="h-4 w-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-foreground">{update.title}</p>
                                  {update.is_new && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-bold bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-full uppercase">
                                      New
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                  {update.description}
                                </p>
                                <p className="text-[10px] text-muted-foreground/70 mt-1">{updateDate}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="px-4 py-2 border-t border-border bg-muted/30">
                    <p className="text-[10px] text-center text-muted-foreground">
                      PRISM is constantly evolving
                    </p>
                  </div>
                </PopoverContent>
              </Popover>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-accent"
                    data-testid="user-menu-btn"
                  >
                    <Avatar className="h-10 w-10 ring-2 ring-background">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-[#2EB4AC] text-white text-sm font-semibold">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-foreground">{user?.name?.split(' ')[0]}</p>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 p-2">
                  <div className="px-2 py-3 border-b border-border mb-2">
                    <p className="font-semibold text-foreground">{user?.name}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuItem className="rounded-lg cursor-pointer">
                    <User className="mr-2 h-4 w-4 text-muted-foreground" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/settings')}
                    className="rounded-lg cursor-pointer"
                  >
                    <Cog className="mr-2 h-4 w-4 text-muted-foreground" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="my-2" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="rounded-lg text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* ============ RIGHT-SIDE WIDGETS DRAWER ============
          A slim collapsible drawer anchored to the right edge. Only a thin
          handle shows by default; clicking it slides in a side panel that
          hosts the three originally-floating widgets (Notes, Notifications,
          Jobs). The underlying FAB buttons are reset from `fixed` to
          `static` via Tailwind descendant selectors so they stack cleanly
          inside the panel. Each widget keeps its own modal/dialog behavior. */}
      <aside
        className={`fixed top-1/2 -translate-y-1/2 right-0 z-50 transition-transform duration-300 ${
          widgetsOpen ? 'translate-x-0' : 'translate-x-[100%]'
        }`}
        aria-hidden={!widgetsOpen}
      >
        <div
          className={`relative bg-white border border-slate-200 rounded-l-2xl shadow-2xl shadow-slate-900/10 py-4 pr-3 pl-4 flex flex-col items-center gap-4
            [&_.notes-fab]:!static [&_.notes-fab]:!bottom-auto [&_.notes-fab]:!right-auto [&_.notes-fab]:!z-0
            [&_.notif-fab]:!static [&_.notif-fab]:!bottom-auto [&_.notif-fab]:!right-auto [&_.notif-fab]:!z-0
            [&_.jobs-fab]:!static [&_.jobs-fab]:!bottom-auto [&_.jobs-fab]:!right-auto [&_.jobs-fab]:!z-0`}
        >
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.14em]">Widgets</p>
          <NotesFab />
          <NotificationBell />
          <JobsFab />
        </div>
      </aside>
      {/* Edge handle — always visible, bumps when drawer is open */}
      <button
        type="button"
        onClick={() => setWidgetsOpen((v) => !v)}
        className={`fixed top-1/2 -translate-y-1/2 z-50 flex items-center justify-center h-16 w-7 rounded-l-xl shadow-lg transition-all duration-300 ${
          widgetsOpen
            ? 'bg-slate-900 text-white right-[88px]'
            : 'bg-white text-slate-600 hover:text-violet-600 hover:bg-violet-50 right-0 border border-r-0 border-slate-200'
        }`}
        aria-expanded={widgetsOpen}
        aria-label={widgetsOpen ? 'Close widgets drawer' : 'Open widgets drawer'}
        data-testid="widgets-drawer-toggle"
        title={widgetsOpen ? 'Hide widgets' : 'Notes · Notifications · Jobs'}
      >
        {widgetsOpen ? <ChevronRight className="h-4 w-4" /> : <ChevronRight className="h-4 w-4 rotate-180" />}
      </button>
      <WhatsNewModal />

      {/* Feature Request Modal */}
      <Dialog open={featureModalOpen} onOpenChange={setFeatureModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Lightbulb className="h-5 w-5 text-white" />
              </div>
              <div>
                <DialogTitle>Request a Feature</DialogTitle>
                <DialogDescription>
                  Have an idea? We'd love to hear it!
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="feature-title">Feature Title</Label>
              <Input
                id="feature-title"
                placeholder="e.g., Dark mode support"
                value={featureTitle}
                onChange={(e) => setFeatureTitle(e.target.value)}
                className="focus-visible:ring-amber-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="feature-description">Description</Label>
              <Textarea
                id="feature-description"
                placeholder="Describe the feature you'd like to see and how it would help you..."
                value={featureDescription}
                onChange={(e) => setFeatureDescription(e.target.value)}
                className="min-h-[120px] focus-visible:ring-amber-500"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setFeatureModalOpen(false)}
              disabled={submittingFeature}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleFeatureSubmit}
              disabled={submittingFeature || !featureTitle.trim() || !featureDescription.trim()}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            >
              {submittingFeature ? (
                <>Submitting...</>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardLayout;
